import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";

const CustomDialog = ({
  open,
  onClose,
  title,
  message,
  type = "error", // Default to 'error', can be 'warning' or 'success'
  primaryButtonText = "OK",
  primaryButtonAction = onClose,
  secondaryButtonText = null,
  secondaryButtonAction = onClose,
}) => {
  // Determine colors based on type
  const colors = {
    error: {
      background: "#FEECEC",
      iconColor: "#FF0000",
      icon: "✖",
    },
    warning: {
      background: "#FFF3CD",
      iconColor: "#FFA500",
      icon: "⚠",
    },
    success: {
      background: "#E6F4EA",
      iconColor: "#28A745",
      icon: "✔",
    },
  };

  const styles = colors[type] || colors.error;

  // Calculate width dynamically
  const calculateDialogWidth = () => {
    const baseWidth = 600; // Minimum width
    const maxWidth = window.innerWidth * 0.9; // 90% of the viewport width
    const additionalWidth = Math.min(message.length * 6, 500); // Adjust for content
    return Math.min(Math.max(baseWidth, additionalWidth), maxWidth) + "px";
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        style: {
          backgroundColor: "#fff",
          padding: "24px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          width: calculateDialogWidth(),
          maxWidth: "90%", // Ensure responsiveness on smaller screens
        },
      }}
    >
      <DialogTitle sx={{ p: 0, mb: 2 }}>
        {/* Icon */}
        <Box
          sx={{
            width: "64px",
            height: "64px",
            margin: "0 auto",
            backgroundColor: styles.background,
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "32px",
              fontWeight: "bold",
              color: styles.iconColor,
            }}
          >
            {styles.icon}
          </Typography>
        </Box>
        <Typography
          variant="h6"
          sx={{ mt: 2, fontWeight: "bold", color: "#333", fontSize: "18px" }}
        >
          {title ||
            (type === "error"
              ? "Error"
              : type === "warning"
              ? "Warning"
              : "Success")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            color: "#666",
            fontSize: "14px",
            lineHeight: "1.6",
            marginBottom: "24px",
            wordWrap: "break-word",
            backgroundColor: styles.background,
          }}
        >
          {message || "There was an issue processing your request."}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {/* Secondary Button */}
        {secondaryButtonText && (
          <Button
            onClick={secondaryButtonAction}
            sx={{
              minWidth: "100px",
              padding: "8px 16px",
              backgroundColor: "#FF0000",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "4px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#cc0000",
              },
            }}
          >
            {secondaryButtonText}
          </Button>
        )}
        {/* Primary Button */}
        {primaryButtonText && (
          <Button
            onClick={primaryButtonAction}
            sx={{
              minWidth: "100px",
              padding: "8px 16px",
              backgroundColor: "#666",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "4px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#007BFF",
              },
            }}
          >
            {primaryButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomDialog;
